import React, { memo } from 'react'
import { Divider, Grid } from '@mui/material'

import CustomDialog from '../shared/customDialog/CustomDialog'
import Text from '../shared/text/Text'
import {
  AMOUNT_BASED_BONUS,
  COMMUNITY_LEADERBOARD_BONUS,
  CURRENCIES,
  INDIVIDUAL_LEADERBOARD_BONUS,
  POINTS_PER_USD,
  REFERRAL_BONUS,
  REWARDS_LOYALTY_BOOSTER,
  REWARDS_TIME_BOOSTER,
  TIME_BASED_BONUS
} from '../../constants/app'
import GridData from '../shared/data/GridData'
import {
  formatMulti,
  formatOrdinal,
  formatPts
} from '../../util/numbers.helpers'
import {
  buildLeaderboardBonusLabel,
  checkLoyaltyBooster
} from '../../util/app.helpers'
import LabelValue from '../shared/text/LabelValue'
import useSaleTimer from '../../hooks/useSaleTimer'
import {
  amountBasedBonusRecord,
  referralBasedBonusRecord,
  timeBasedBonusRecord
} from '../../util/points-bonuses.helpers'

const RewardsBoostersPopup = ({
  t,
  onSubmit,
  onAutoClose,
  onClose,
  mainProfile: { nftCount, finalMs, timeBasedPositionFinal },
  saleEnded
}) => {
  const { value } = useSaleTimer(saleEnded)

  let timeBooster = 0
  const timerBoosterValue = !saleEnded && value
  const timeBoostersData = Object.keys(REWARDS_TIME_BOOSTER).map((key) => {
    const value = REWARDS_TIME_BOOSTER[key]
    const isCurrent = value === timerBoosterValue
    if (isCurrent) {
      timeBooster = value
    }
    return {
      label:
        key === '121'
          ? t('home.deposit-after-the-first')
          : key === '180'
            ? t('home.deposit-in-the-last')
            : t('home.deposit-in-the-first', { minutes: key }),
      value: formatMulti(value),
      isCurrent
    }
  })

  let loyaltyBooster = 0
  const loyaltyBoosterValue = checkLoyaltyBooster(nftCount)
  const loyaltyBoostersData = Object.keys(REWARDS_LOYALTY_BOOSTER)
    .sort((a, b) => b - a)
    .map((key) => {
      const value = REWARDS_LOYALTY_BOOSTER[key]
      const isCurrent = value === loyaltyBoosterValue
      if (isCurrent) {
        loyaltyBooster = value
      }
      return {
        label: t('home.you-hold-qbx-founders-key', { value: key }),
        value: formatMulti(value),
        isCurrent
      }
    })

  const totalBooster = timeBooster + loyaltyBooster

  const timeBasedBonusesData = Object.keys(TIME_BASED_BONUS).map((key) => {
    return timeBasedBonusRecord(
      t,
      key,
      TIME_BASED_BONUS[key],
      key === timeBasedPositionFinal
    )
  })

  const amountBasedBonusesData = Object.keys(AMOUNT_BASED_BONUS).map((key) => {
    return amountBasedBonusRecord(
      t,
      key,
      AMOUNT_BASED_BONUS[key],
      `+${key} ${CURRENCIES.DEFAULT}`,
      AMOUNT_BASED_BONUS[key] === finalMs && finalMs?.amount[key]
    )
  })

  const referralBonusesData = Object.keys(REFERRAL_BONUS).map((key) => {
    return referralBasedBonusRecord(
      t,
      key,
      REFERRAL_BONUS[key],
      `${key} wallets`,
      REFERRAL_BONUS[key] === finalMs && finalMs?.ref[key]
    )
  })

  const leaderboardBonusesData = Object.keys(INDIVIDUAL_LEADERBOARD_BONUS).map(
    (key) => {
      const value = INDIVIDUAL_LEADERBOARD_BONUS[key]
      return {
        label: buildLeaderboardBonusLabel(t, key),
        value: formatPts(value)
      }
    }
  )

  const communitiesBonusesData = Object.keys(COMMUNITY_LEADERBOARD_BONUS).map(
    (key) => {
      const value = COMMUNITY_LEADERBOARD_BONUS[key]
      return {
        label: t('home.place-community', { position: formatOrdinal(key) }),
        value: t('home.per-community-wallet', { points: formatPts(value) })
      }
    }
  )

  return (
    <CustomDialog
      isForm
      title={t('home.points-bonuses')}
      description={t('home.points-bonuses-desc')}
      open
      onClose={onClose}
      cancelProps={{ label: t('home.check-out-leaderboards'), autoWidth: true }}
      submitProps={{
        label: t('common.close'),
        autoWidth: true
      }}
      onAutoClose={onAutoClose}
      onSubmit={onSubmit}
      body={
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid container spacing={{ xs: 1, md: 4 }}>
              <Grid item xs={12} sm={6}>
                <LabelValue
                  label={t('home.deposit-points-earned-per')}
                  value={formatPts(POINTS_PER_USD)}
                  fontSize={1.125}
                  sx={{ justifyContent: 'space-between' }}
                  valueProps={{ fontWeight: 900 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <LabelValue
                  label={t('home.your-current-total-multiplier')}
                  value={formatMulti(totalBooster)}
                  fontSize={1.125}
                  sx={{ justifyContent: 'space-between' }}
                  valueProps={{ fontWeight: 900 }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ mb: { xs: 2, md: 3 }, mt: { xs: 1, md: 1 } }} />
            <Grid container spacing={{ xs: 1, md: 4 }}>
              <Grid item xs={12} sm={6}>
                <Text
                  label={t('home.time-based-multipliers')}
                  fontWeight={700}
                  sx={{ mb: 0.9 }}
                />
                <GridData
                  data={timeBoostersData}
                  sx={{ justifyContent: 'space-between' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Text
                  label={t('home.founders-key-multipliers')}
                  fontWeight={700}
                  sx={{ mb: 0.9 }}
                />
                <GridData
                  data={loyaltyBoostersData}
                  sx={{ justifyContent: 'space-between' }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ mb: { xs: 2, md: 3 }, mt: { xs: 1, md: 1 } }} />
            <Grid container spacing={{ xs: 1, md: 4 }}>
              <Grid item xs={12} sm={6}>
                <Text
                  label={t('home.time-based-deposit-bonuses')}
                  fontWeight={700}
                  sx={{ mb: 0.9 }}
                />
                <GridData
                  data={timeBasedBonusesData}
                  sx={{ justifyContent: 'space-between' }}
                />
                <Text
                  variant='body1'
                  component='span'
                  label={t('home.wallet-can-only-win-once')}
                  fontSize='0.75rem'
                  sx={{ mt: 4 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Text
                  label={t('home.amount-based-deposit-bonuses')}
                  fontWeight={700}
                  sx={{ mb: 0.9 }}
                />
                <GridData
                  data={amountBasedBonusesData}
                  sx={{ justifyContent: 'space-between' }}
                />
                <Text
                  variant='body1'
                  component='span'
                  label={t('home.to-win-these-bonuses')}
                  fontSize='0.75rem'
                  sx={{ mt: 4 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Text
                  label={t('home.referral-bonuses')}
                  fontWeight={700}
                  sx={{ mb: 0.9 }}
                />
                <GridData
                  data={referralBonusesData}
                  sx={{ justifyContent: 'space-between' }}
                />
                <Text
                  variant='body1'
                  component='span'
                  label={t('home.wallets-only-count')}
                  fontSize='0.75rem'
                  sx={{ mt: 4 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Text
                  label={t('home.individual-leaderboard-bonuses')}
                  fontWeight={700}
                  sx={{ mb: 0.9 }}
                />
                <GridData
                  data={leaderboardBonusesData}
                  sx={{ justifyContent: 'space-between' }}
                />
                <Text
                  variant='body1'
                  component='span'
                  label={t('home.click-the-button-to-leaderboards')}
                  fontSize='0.75rem'
                  sx={{ mt: 4 }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ mb: { xs: 2, md: 3 }, mt: { xs: 1, md: 1 } }} />
            <Text
              label={t('home.top-communities-deposit-bonuses')}
              fontWeight={700}
              sx={{ mb: 0.9 }}
            />
            <GridData
              data={communitiesBonusesData}
              sx={{ justifyContent: 'space-between' }}
            />
          </Grid>
        </Grid>
      }
      t={t}
      maxWidth='lg'
    />
  )
}

export default memo(RewardsBoostersPopup)
